import styled from 'styled-components'
import { useState, useEffect, useContext } from 'react'
import { TrackingContext } from 'contexts'

/**
 * This component display an article with an image, and a link to the full article.
 * It handles image loading with a fallback mechanism and tracks events using a context.
 *
 * @param {string} props.metadataId - A unique identifier for the article Unity metadata.
 * @param {string} props.title - The title of the article.
 * @param {string} props.header - The header or subtitle of the article.
 * @param {string} props.imageSrc - The source URL of the article's image.
 * @param {boolean} props.isPremium - Indicates if the article is premium content.
 * @param {string} props.targetUrl - The URL to navigate to when the article is clicked.
 * @returns {JSX.Element} The rendered SourceArticle component.
 */
export const SourceArticle = ({
  metadataId,
  title,
  header,
  imageSrc,
  isPremium,
  targetUrl,
}: {
  metadataId: string
  title: string
  header: string
  imageSrc: string
  isPremium: boolean
  targetUrl: string
}) => {
  const trackEvent = useContext(TrackingContext)
  const imageFallbackUrl =
    'https://interaktiv-mf.tagesanzeiger.ch/2023/series-streaming/static/media/imageFallback.213e8a8172f16049f4c2.jpg'
  const [imageUrl, setImageUrl] = useState<string>(imageFallbackUrl)

  useEffect(() => {
    const image = new Image()
    image.src = imageSrc
    image.onload = () => {
      // No idea why we have this 404 image here but
      // we want to replace it with the fallback image
      if (
        image.src ===
        'https://feed-prod.unitycms.io/images/FgHwHRU8a8i9oro-PLG8iE.jpg'
      ) {
        setImageUrl(imageFallbackUrl)
        return
      }

      setImageUrl(imageSrc)
    }
    image.onerror = () => {
      console.log('Image error')
    }
  }, [imageSrc])

  const handleArticleClick = () => {
    // Track the click event
    trackEvent &&
      trackEvent('teaser_click', {
        teaser_article_id: metadataId,
        teaser_article_premium: isPremium,
        teaser_source: 'streaming_search_ai',
      })
  }

  return (
    <Article href={targetUrl} target='_blank' onClick={handleArticleClick}>
      <Content>
        <ImageContainer style={{ backgroundImage: `url(${imageUrl})` }} />
        <Text>
          <TitleHeader>
            {isPremium && <Abo />}
            {header}
          </TitleHeader>
          <Title>{title}</Title>
        </Text>
      </Content>
    </Article>
  )
}

const Article = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.colorText};
  display: block;
  flex: 0 0 auto;
  width: 100%;
  white-space: normal;
  margin-right: 1rem;
  border-right: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colorGrey};
  padding-bottom: 1rem;
  margin-bottom: 1rem;

  &:last-child {
    border: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }
`

const TitleHeader = styled.div`
  font-size: 1.25rem;
  line-height: 1.2;
  margin-bottom: 0.5rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: none;
`

const Content = styled.div`
  display: flex;
`

const Title = styled.div`
  font-size: 1.45rem;
  line-height: 1.2;
`

const Text = styled.div`
  margin-left: 1rem;
`

const ImageContainer = styled.div`
  width: 50px;
  height: 50px;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`

const Abo = styled.div`
  display: inline-block;
  margin-right: 0.5rem;
  -webkit-font-smoothing: antialiased;

  &::after {
    display: block;
    content: 'Abo';
    font-size: 1rem;
    padding: 0.2rem 0.5rem 0.15rem 0.5rem;
    text-align: center;
    position: relative;
    color: ${({ theme }) => theme.colorTextInverted};
    background-color: ${({ theme }) => theme.colorText};
  }
`
